import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import toast from 'react-hot-toast'
import { assegnazioniCantiereApi } from '../services/api'

interface ConsegnaMezzoModalProps {
  assegnazioneId: number
  modalita: 'presa' | 'riconsegna'
  kmMinimi?: number
  onClose: () => void
  onSuccess: () => void
}

const OPZIONI_CARBURANTE = [
  { value: '1_4', label: '1/4' },
  { value: '1_2', label: '1/2' },
  { value: '3_4', label: '3/4' },
  { value: 'pieno', label: 'Pieno' },
]

export default function ConsegnaMezzoModal({ assegnazioneId, modalita, kmMinimi, onClose, onSuccess }: ConsegnaMezzoModalProps) {
  const [km, setKm] = useState('')
  const [carburante, setCarburante] = useState('pieno')
  const [danni, setDanni] = useState('')

  const mutation = useMutation({
    mutationFn: (): Promise<AxiosResponse<any>> => {
      const kmValue = parseInt(km, 10)
      if (modalita === 'presa') {
        return assegnazioniCantiereApi.presaConsegna(assegnazioneId, {
          km_presa_consegna: kmValue,
          carburante_presa_consegna: carburante,
          danni_presa_consegna: danni || undefined,
        })
      }
      return assegnazioniCantiereApi.riconsegna(assegnazioneId, {
        km_riconsegna: kmValue,
        carburante_riconsegna: carburante,
        danni_riconsegna: danni || undefined,
      })
    },
    onSuccess: () => {
      toast.success(modalita === 'presa' ? 'Presa in consegna confermata' : 'Riconsegna confermata')
      onSuccess()
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Errore durante il salvataggio')
    },
  })

  const kmValido = km !== '' && !isNaN(parseInt(km, 10)) && (kmMinimi === undefined || parseInt(km, 10) >= kmMinimi)

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[300]">
      <div className="card w-full max-w-md p-6">
        <h2 className="text-lg font-semibold mb-4">
          {modalita === 'presa' ? 'Presa in consegna mezzo' : 'Riconsegna mezzo'}
        </h2>

        <label className="label">Km attuali</label>
        <input
          className="input mb-1"
          type="number"
          value={km}
          onChange={(e) => setKm(e.target.value)}
          placeholder={kmMinimi !== undefined ? `>= ${kmMinimi}` : ''}
        />
        {kmMinimi !== undefined && km !== '' && !kmValido && (
          <p className="text-sm text-[var(--mac-red)] mb-2">I km devono essere &gt;= {kmMinimi}</p>
        )}

        <label className="label mt-3">Carburante presente</label>
        <select className="select mb-3" value={carburante} onChange={(e) => setCarburante(e.target.value)}>
          {OPZIONI_CARBURANTE.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <label className="label">Danni da segnalare (opzionale)</label>
        <textarea
          className="input mb-4"
          rows={3}
          value={danni}
          onChange={(e) => setDanni(e.target.value)}
          placeholder="Descrivi eventuali danni riscontrati"
        />

        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose}>Annulla</button>
          <button
            className="btn-primary"
            disabled={!kmValido || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Invio...' : 'Conferma'}
          </button>
        </div>
      </div>
    </div>
  )
}
